import { Box, Button, Flex, Icon, Progress, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable
} from '@tanstack/react-table';
// Custom components
import Card from 'components/card/Card';
import * as React from 'react';
import Moment from 'react-moment';
import { Link as RouterLink } from 'react-router-dom';
// Assets
import { MdCancel, MdOutlineError } from 'react-icons/md';
import { ForfaitToRenewRow, RenewStatus } from 'views/admin/default/dashboardData';

const ROWS_SHOWN = 5;

const STATUS_STYLE: Record<RenewStatus, { color: string; icon: typeof MdCancel }> = {
	'Épuisé': { color: 'red.500', icon: MdCancel },
	'< 1h': { color: 'orange.500', icon: MdOutlineError }
};

const columnHelper = createColumnHelper<ForfaitToRenewRow>();

// Forfaits horaires à renouveler, du plus au moins consommé
export default function ForfaitsToRenew(props: { rows: ForfaitToRenewRow[] }) {
	const { rows } = props;
	// Les plus urgents sont retenus avant le tri : trier une colonne ne fait que les réordonner
	const shownRows = React.useMemo(() => rows.slice(0, ROWS_SHOWN), [ rows ]);
	const hiddenCount = rows.length - shownRows.length;
	const [ sorting, setSorting ] = React.useState<SortingState>([]);
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
	const header = (label: string) => (
		<Text justifyContent='space-between' align='center' fontSize={{ sm: '10px', lg: '12px' }} color='gray.400'>
			{label}
		</Text>
	);
	const columns = [
		columnHelper.accessor('name', {
			id: 'name',
			header: () => header('CLIENT'),
			cell: (info) => (
				<Flex align='center'>
					<Text color={textColor} fontSize='sm' fontWeight='700'>
						{info.getValue()}
					</Text>
				</Flex>
			)
		}),
		columnHelper.accessor('status', {
			id: 'status',
			header: () => header('ÉTAT'),
			cell: (info) => (
				<Flex align='center'>
					<Icon
						w='24px'
						h='24px'
						me='5px'
						color={STATUS_STYLE[info.getValue()].color}
						as={STATUS_STYLE[info.getValue()].icon}
					/>
					<Text color={textColor} fontSize='sm' fontWeight='700'>
						{info.getValue()}
					</Text>
				</Flex>
			)
		}),
		columnHelper.accessor('lastIntervention', {
			id: 'lastIntervention',
			header: () => header('DERNIÈRE INTERVENTION'),
			cell: (info) => (
				<Text color={textColor} fontSize='sm' fontWeight='700'>
					{info.getValue() ? <Moment format='DD/MM/YYYY'>{info.getValue()}</Moment> : '—'}
				</Text>
			)
		}),
		columnHelper.accessor('progress', {
			id: 'progress',
			header: () => header('CONSOMMÉ'),
			cell: (info) => (
				<Flex align='center'>
					<Progress
						variant='table'
						colorScheme={info.getValue() >= 90 ? 'red' : 'brandScheme'}
						h='8px'
						w='108px'
						value={info.getValue()}
					/>
					<Text color={textColor} fontSize='sm' fontWeight='700' ms='8px'>
						{info.getValue()} %
					</Text>
				</Flex>
			)
		})
	];
	const table = useReactTable({
		data: shownRows,
		columns,
		state: {
			sorting
		},
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getRowId: (row) => row.id
	});
	return (
		<Card flexDirection='column' w='100%' px='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
			<Flex px='25px' mb='8px' justifyContent='space-between' align='center'>
				<Text color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
					Forfaits à renouveler
				</Text>
				<Button as={RouterLink} to='/admin/forfaits' size='sm' variant='ghost' colorScheme='teal'>
					Voir les forfaits
				</Button>
			</Flex>
			{rows.length > 0 ? (
				<Box>
					<Table variant='simple' color='gray.500' mb='24px' mt='12px'>
						<Thead>
							{table.getHeaderGroups().map((headerGroup) => (
								<Tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<Th
												key={header.id}
												colSpan={header.colSpan}
												pe='10px'
												borderColor={borderColor}
												cursor='pointer'
												onClick={header.column.getToggleSortingHandler()}>
												<Flex
													justifyContent='space-between'
													align='center'
													fontSize={{ sm: '10px', lg: '12px' }}
													color='gray.400'>
													{flexRender(header.column.columnDef.header, header.getContext())}
												</Flex>
											</Th>
										);
									})}
								</Tr>
							))}
						</Thead>
						<Tbody>
							{table.getRowModel().rows.map((row) => {
								return (
									<Tr key={row.id}>
										{row.getVisibleCells().map((cell) => {
											return (
												<Td
													key={cell.id}
													fontSize={{ sm: '14px' }}
													minW={{ sm: '150px', md: '200px', lg: 'auto' }}
													borderColor='transparent'>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</Td>
											);
										})}
									</Tr>
								);
							})}
						</Tbody>
					</Table>
					{hiddenCount > 0 && (
						<Text px='25px' pb='24px' color='secondaryGray.600' fontSize='sm'>
							Et {hiddenCount} autre{hiddenCount > 1 ? 's' : ''} forfait{hiddenCount > 1 ? 's' : ''} à renouveler
						</Text>
					)}
				</Box>
			) : (
				<Text px='25px' pb='24px' color='secondaryGray.600' fontSize='sm'>
					Aucun forfait à renouveler
				</Text>
			)}
		</Card>
	);
}
